import { ParsedUrlQuery } from 'querystring';

import { publicConfig } from '@public-config';

import { Address } from 'models';
import { EmailHelpers } from 'helpers/EmailHelpers';

const URLHelpers = {
    /**
     * Returns the full CDN URL for a file.
     * @param path
     */
    file: (path: string) => {
        return publicConfig.urls.files + (path.startsWith('/') ? path : `/${path}`);
    },

    frontend: (path: string) => {
        return publicConfig.urls.frontend + (path.startsWith('/') ? path : `/${path}`);
    },

    getDomain: (url: string): string | null => {
        try {
            return new URL(url.trim()).hostname.replace(/^www\./, '');
        } catch {
            return null;
        }
    },

    normalizeWebsiteUrl: (url: string): string => {
        let normalized = url.toLowerCase().replace(/\/+$/, '');

        if (!/^https?:\/\//i.test(normalized)) {
            normalized = `http://${normalized}`;
        }

        return normalized;
    },

    emailMatchesWebsite: (email: string, website: string): boolean => {
        const emailDomain = EmailHelpers.getDomain(email);
        const websiteDomain = URLHelpers.getDomain(website);

        if (!emailDomain || !websiteDomain) {
            return false;
        }

        return emailDomain === websiteDomain || emailDomain.endsWith(`.${websiteDomain}`);
    },

    getServerSideParam: (params: ParsedUrlQuery = {}, key: string) => {
        const value = params[key];

        if (value && typeof value === 'string') {
            return value;
        }

        return null;
    },

    og: (logo: string) => {
        return `${publicConfig.urls.frontend}/api/og?logo=${encodeURIComponent(logo)}`;
    },

    /**
     * Google Maps search for a location. Geocoded addresses search the terms (such as the company
     * name) centred on their coordinates, since a free-text address search can land on an
     * unrelated place (DEV-10845). Addresses without coordinates fall back to a text search.
     */
    maps: (address: Address | null | undefined, search?: string): string | undefined => {
        const hasGeocodedCoordinates = ([longitude, latitude]: [number, number]) => longitude !== 0 || latitude !== 0;

        if (address?.coordinates && hasGeocodedCoordinates(address.coordinates) && search?.trim()) {
            const [longitude, latitude] = address.coordinates;

            return `https://www.google.com/maps/search/${encodeURIComponent(search.trim())}/@${latitude},${longitude},17z`;
        }

        const { street, number, postalCode, city, state, country } = address ?? {};
        const query = [search, street, number, postalCode, city, state, country]
            .map((part) => part?.trim())
            .filter(Boolean)
            .join(', ');

        return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : undefined;
    },

    pathMatches: (pathname: string, routes: string[]) =>
        routes.some((route) => pathname === route || pathname.startsWith(`${route}/`)),
};

export { URLHelpers };
